import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Save, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Canonical role keys (sent to backend) + display labels / short codes
const ROLES = [
  { key: "Wicketkeeper", code: "WK", label: "Wicket-Keeper" },
  { key: "Batsman", code: "BAT", label: "Batsman" },
  { key: "All-Rounder", code: "AR", label: "All-Rounder" },
  { key: "Bowler", code: "BOWL", label: "Bowler" },
];

const DEFAULTS = {
  Wicketkeeper: { min: 1, max: 4 },
  Batsman: { min: 3, max: 6 },
  "All-Rounder": { min: 1, max: 4 },
  Bowler: { min: 3, max: 6 },
};

export default function MatchDayCompositionRules({ activeTournament, getAuthHeader, setSuccess, setError }) {
  const tournamentId = activeTournament?.tournament_id || activeTournament?.id || "";
  const [rules, setRules] = useState(DEFAULTS);
  const [isSet, setIsSet] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!tournamentId) return;
    setLoading(true);
    try {
      const headers = await getAuthHeader();
      const res = await axios.get(
        `${API}/admin/tournaments/${tournamentId}/daily-composition-rules`,
        { headers }
      );
      const r = res.data?.daily_composition_rules || {};
      setRules({
        Wicketkeeper: { min: r.Wicketkeeper?.min ?? 1, max: r.Wicketkeeper?.max ?? 4 },
        Batsman: { min: r.Batsman?.min ?? 3, max: r.Batsman?.max ?? 6 },
        "All-Rounder": { min: r["All-Rounder"]?.min ?? 1, max: r["All-Rounder"]?.max ?? 4 },
        Bowler: { min: r.Bowler?.min ?? 3, max: r.Bowler?.max ?? 6 },
      });
      setIsSet(!!res.data?.is_set);
    } catch (e) {
      // leave defaults
    } finally {
      setLoading(false);
    }
  }, [tournamentId, getAuthHeader]);

  useEffect(() => {
    load();
  }, [load]);

  const update = (roleKey, field, value) => {
    const num = value === "" ? "" : Math.max(0, parseInt(value, 10) || 0);
    setRules((prev) => ({ ...prev, [roleKey]: { ...prev[roleKey], [field]: num } }));
  };

  const sumMin = ROLES.reduce((s, r) => s + (parseInt(rules[r.key].min, 10) || 0), 0);
  const sumMax = ROLES.reduce((s, r) => s + (parseInt(rules[r.key].max, 10) || 0), 0);
  const minValid = sumMin <= 11;
  const maxValid = sumMax >= 11;
  const rangesValid = ROLES.every((r) => {
    const mn = parseInt(rules[r.key].min, 10);
    const mx = parseInt(rules[r.key].max, 10);
    return Number.isFinite(mn) && Number.isFinite(mx) && mn >= 0 && mx >= mn;
  });
  const canSave = minValid && maxValid && rangesValid && !!tournamentId;

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setError("");
    try {
      const body = {};
      ROLES.forEach((r) => {
        body[r.code] = {
          min: parseInt(rules[r.key].min, 10) || 0,
          max: parseInt(rules[r.key].max, 10) || 0,
        };
      });
      const headers = await getAuthHeader();
      await axios.put(
        `${API}/admin/tournaments/${tournamentId}/daily-composition-rules`,
        body,
        { headers }
      );
      setIsSet(true);
      setSuccess("Match Day composition rules saved");
      setTimeout(() => setSuccess(""), 4000);
    } catch (e) {
      setError(e.response?.data?.detail || "Failed to save match day rules");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-slate-900/50 rounded-lg p-3" data-testid="matchday-composition-section">
      <div className="flex items-center justify-between mb-2">
        <p className="text-slate-400 text-xs uppercase tracking-wider">Match Day Composition Rules</p>
        {isSet ? (
          <span className="px-2 py-0.5 bg-amber-500/20 text-amber-400 rounded text-xs font-medium" data-testid="matchday-composition-configured-badge">
            Configured
          </span>
        ) : (
          <span className="px-2 py-0.5 bg-slate-700/40 text-slate-400 rounded text-xs font-medium" data-testid="matchday-composition-default-badge">
            Using defaults
          </span>
        )}
      </div>
      <p className="text-slate-500 text-xs mb-3">
        Min/max players per role for <strong>Match Day</strong> teams on{" "}
        {activeTournament?.name || "the active tournament"}. Sum of minimums must be ≤ 11 and sum of
        maximums must be ≥ 11. Saved separately from season-team rules.
      </p>

      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 text-sm py-3">
          <Loader2 className="animate-spin" size={16} /> Loading…
        </div>
      ) : (
        <div className="space-y-2">
          <div className="grid grid-cols-[1fr_auto_auto] gap-2 items-center text-xs text-slate-500 px-1">
            <span>Role</span>
            <span className="w-16 text-center">Min</span>
            <span className="w-16 text-center">Max</span>
          </div>
          {ROLES.map((r) => {
            const mn = parseInt(rules[r.key].min, 10);
            const mx = parseInt(rules[r.key].max, 10);
            const rowBad = !(Number.isFinite(mn) && Number.isFinite(mx) && mn >= 0 && mx >= mn);
            return (
              <div key={r.key} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                <span className="text-white text-sm">
                  {r.label} <span className="text-slate-500">({r.code})</span>
                </span>
                <input
                  type="number"
                  min={0}
                  value={rules[r.key].min}
                  onChange={(e) => update(r.key, "min", e.target.value)}
                  className={`w-16 px-2 py-1.5 bg-slate-900 border rounded-lg text-white text-sm text-center ${rowBad ? "border-red-500" : "border-slate-600"}`}
                  data-testid={`matchday-comp-${r.code.toLowerCase()}-min`}
                />
                <input
                  type="number"
                  min={0}
                  value={rules[r.key].max}
                  onChange={(e) => update(r.key, "max", e.target.value)}
                  className={`w-16 px-2 py-1.5 bg-slate-900 border rounded-lg text-white text-sm text-center ${rowBad ? "border-red-500" : "border-slate-600"}`}
                  data-testid={`matchday-comp-${r.code.toLowerCase()}-max`}
                />
              </div>
            );
          })}

          <div className="flex items-center justify-between pt-2 text-xs">
            <span className={minValid ? "text-slate-400" : "text-red-400"} data-testid="matchday-comp-summin">
              Σ min: {sumMin} {minValid ? "≤ 11 ✓" : "> 11 ✗"}
            </span>
            <span className={maxValid ? "text-slate-400" : "text-red-400"} data-testid="matchday-comp-summax">
              Σ max: {sumMax} {maxValid ? "≥ 11 ✓" : "< 11 ✗"}
            </span>
          </div>

          <button
            onClick={save}
            disabled={!canSave || saving}
            className="w-full mt-2 px-4 py-2 bg-amber-500 text-black rounded-lg text-sm font-semibold hover:bg-amber-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
            data-testid="matchday-comp-save-btn"
          >
            {saving ? <Loader2 className="animate-spin" size={16} /> : <Save size={16} />}
            Save Match Day Rules
          </button>
        </div>
      )}
    </div>
  );
}
